'use client';

import React from 'react';
import { useIntl } from 'react-intl';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { HStack, Box, Icon, Text, VStack, Span } from '@ui-library';

import useLocalization from '@/hooks/useLocalization';
import {
  CheckInSettings,
  ReservationWithRateDetailsDTO,
  Settings,
} from '@/models';
import MoonSvg from '@/components/icons/Moon';
import GuestSvg from '@/components/icons/Guest';
import ChildSvg from '@/components/icons/Child';
import { ChevronRight } from '@ui-library/icons';

import { getLongDateFormat } from '@/lib/dateFormat';
import { EditReservation } from '../edit-reservation';
import { getRoomTitle } from '../../helper/reservationHelper';
import { getNightCount, getDateInterval } from '../utils';
interface ReservationDetailProps {
  quantity: number | string;
  icon: React.ReactElement;
}

const ReservationDetail = ({
  quantity,
  icon,
}: ReservationDetailProps): React.ReactElement => {
  return (
    <HStack background={'fog.100'} p={1} borderRadius={'md'}>
      <Text fontWeight={'bold'}>{quantity}</Text>
      {icon}
    </HStack>
  );
};

interface ReservationDetailsCardProps {
  reservation: ReservationWithRateDetailsDTO;
  primaryColor: string;
  onClick?: (reservation: ReservationWithRateDetailsDTO) => void;
  listLayout?: boolean;
  companyTimezone: string;
  checkInSettings?: CheckInSettings | null;
  showEditButton: boolean;
  settings?: Settings;
  reservations?: ReservationWithRateDetailsDTO[];
}

const getTitle = (
  reservation: ReservationWithRateDetailsDTO,
  intl: any,
  displayGuestRoomNumber = true,
): string => {
  const hasMultipleRooms = reservation.rooms.length > 1;
  const room = reservation.rooms?.[0];

  return hasMultipleRooms
    ? intl.formatMessage(
        {
          id: 'common/accommodations',
          defaultMessage: '{n} accommodations',
        },
        { n: reservation.rooms.length },
      )
    : getRoomTitle(room, intl, displayGuestRoomNumber);
};

export const ReservationDetailsCard = ({
  reservation,
  primaryColor,
  onClick,
  listLayout = false,
  companyTimezone,
  checkInSettings,
  showEditButton = false,
  settings,
  reservations = [],
}: ReservationDetailsCardProps): React.ReactElement => {
  const intl = useIntl();
  const router = useRouter();
  const { dateFnsLocale } = useLocalization();
  const searchParams = useSearchParams();
  const showEditDrawer = searchParams.get('mode') === 'edit';
  const hasReservationId = searchParams.has('reservationId');
  const params = useParams();
  const { company, guestBookSlug, locale } = params;

  const handleReservationClick = () => {
    if (showEditButton || hasReservationId) {
      history.pushState(null, '', `?reservationId=${reservation.id}&mode=edit`);
      return;
    }

    history.pushState(null, '', `?reservationId=${reservation.id}`);

    onClick?.(reservation);
  };

  const handleDrawerClose = (isCancelledReservation: boolean) => {
    const routeOnClose = isCancelledReservation
      ? `/${locale}/${company}/${guestBookSlug}`
      : `?reservationId=${reservation.id}`;

    router.push(routeOnClose);

    if (isCancelledReservation) {
      router.refresh();
    }
  };

  const renderDates = () => {
    if (!listLayout) return null;

    const format = settings?.date_format
      ? getLongDateFormat(settings.date_format)
      : undefined;

    return (
      <Text fontWeight="bold" color={primaryColor} fontSize="md">
        {getDateInterval(reservation, companyTimezone, dateFnsLocale, format)}
      </Text>
    );
  };

  const renderTitle = (title: string) => <Text>{title}</Text>;

  const renderDetails = (
    details: Pick<
      ReservationWithRateDetailsDTO,
      'startDate' | 'endDate' | 'adults' | 'children'
    >,
  ) => (
    <HStack>
      <ReservationDetail
        quantity={getNightCount(
          details.startDate,
          details.endDate,
          companyTimezone,
        )}
        icon={<MoonSvg />}
      />
      <ReservationDetail
        quantity={details.adults ? Number(details.adults) : 0}
        icon={<GuestSvg />}
      />
      <ReservationDetail
        quantity={details.children ? Number(details.children) : 0}
        icon={<ChildSvg />}
      />
    </HStack>
  );

  const renderReservationId = () => {
    if (listLayout) return null;

    return (
      <>
        <Text onClick={(e) => e.stopPropagation()}>
          {intl.formatMessage({
            id: 'common/reservation_id',
            defaultMessage: 'Reservation ID:',
          })}
          <Span fontWeight="bold" marginLeft={1}>
            {reservation.id}
          </Span>
        </Text>
      </>
    );
  };

  return (
    <Box
      w={'full'}
      borderRadius={'2xl'}
      border={'1px solid'}
      borderColor={'fog.300'}
      onClick={handleReservationClick}
      cursor="pointer"
      _hover={{
        borderColor: 'lightning.400',
        backgroundColor: 'lightning.50',
      }}
      p={4}
    >
      <HStack w={'full'} justifyContent={'space-between'}>
        <VStack gap={2} alignItems={'flex-start'} w={'full'}>
          {renderDates()}
          {renderTitle(
            getTitle(
              reservation,
              intl,
              checkInSettings?.display_guest_room_number ?? false,
            ),
          )}
          {renderDetails(reservation)}
          {renderReservationId()}
        </VStack>
        {showEditButton && settings ? (
          <EditReservation
            isOpen={showEditDrawer}
            onClose={handleDrawerClose}
            checkInSettings={checkInSettings || null}
            reservations={reservations}
            settings={settings}
          />
        ) : (
          <Icon as={ChevronRight} mr={4} />
        )}
      </HStack>
    </Box>
  );
};
